import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { Minus, Plus, PhoneCall, AlertTriangle, CreditCard, Loader2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { useTheme } from '../../context/ThemeContext';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');

export const celebrate = () => {
  confetti({ particleCount: 140, spread: 80, origin: { y: 0.6 }, colors: ['#14b8a6', '#22d3ee', '#fbbf24', '#a78bfa'] });
  setTimeout(() => confetti({ particleCount: 90, spread: 110, origin: { y: 0.5 } }), 250);
};

const Stepper = ({ value, min, max, onChange }) => (
  <div className="flex items-center gap-2" data-testid="call-lines-stepper">
    <button type="button" disabled={value <= min} onClick={() => onChange(value - 1)}
      className="w-9 h-9 rounded-lg border border-gray-400/30 flex items-center justify-center text-gray-500 hover:bg-gray-500/10 disabled:opacity-30 transition-colors"
      data-testid="call-lines-decrease"><Minus size={15} /></button>
    <span className="w-10 text-center text-lg font-semibold tabular-nums" data-testid="call-lines-value">{value}</span>
    <button type="button" disabled={value >= max} onClick={() => onChange(value + 1)}
      className="w-9 h-9 rounded-lg border border-gray-400/30 flex items-center justify-center text-gray-500 hover:bg-gray-500/10 disabled:opacity-30 transition-colors"
      data-testid="call-lines-increase"><Plus size={15} /></button>
  </div>
);

const Row = ({ label, value, strong, testid }) => (
  <div className={`flex items-center justify-between text-sm ${strong ? 'font-semibold' : 'text-gray-500'}`} data-testid={testid}>
    <span>{label}</span><span className="tabular-nums">{value}</span>
  </div>
);

const Summary = ({ info, extra, hasSub }) => {
  const cur = info.subscription.active ? info.subscription.lines : 0;
  const diff = extra - cur;
  if (diff === 0 && !info.subscription.cancel_at_period_end) return null;
  const price = info.price_per_line;
  const keep = info.subscription.keep_until && new Date(info.subscription.keep_until) > new Date() ? (info.subscription.keep_extra || 0) : 0;
  const prepaid = hasSub && keep > cur && extra <= keep;
  return (
    <div className="rounded-xl bg-gray-500/5 border border-gray-400/20 p-5 space-y-3" data-testid="call-lines-summary">
      <p className="text-sm font-medium">Summary of changes</p>
      <Row label={`${diff >= 0 ? 'Add' : 'Remove'} ${Math.abs(diff)} line${Math.abs(diff) === 1 ? '' : 's'} at ${money(price)}/mo`}
        value={`${money(cur * price)} → ${money(extra * price)}`} />
      {diff > 0 && <Row label={prepaid ? 'Already paid for this period' : hasSub ? 'Charged today to your card (prorated for this period)' : 'Due today on secure Stripe checkout'}
        value={prepaid ? money(0) : hasSub ? 'Prorated' : money(diff * price)} />}
      {diff < 0 && <Row label="Takes effect" value={`End of period ${fmtDate(info.subscription.period_end)}`} />}
      <Row label="New monthly subtotal for call lines" value={money(extra * price)} strong testid="call-lines-monthly-total" />
    </div>
  );
};

export const CallLinesAddon = ({ workspaceId }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [extra, setExtra] = useState(0);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const { isDarkMode } = useTheme();
  const confirmedRef = useRef(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/api/workspaces/${workspaceId}/concurrency`);
    setInfo(data);
    setExtra(data.subscription.active ? data.subscription.lines : 0);
    return data;
  }, [api, workspaceId]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  useEffect(() => {
    const sid = params.get('session_id');
    if (params.get('lines') === 'success' && sid) {
      if (confirmedRef.current) return;
      confirmedRef.current = true;
      api.get(`/api/workspaces/${workspaceId}/call-lines/confirm`, { params: { session_id: sid } })
        .then(({ data }) => {
          setInfo(data); setExtra(data.subscription.lines);
          if (data.paid) { celebrate(); toast.success(`You now have ${data.limit} concurrent call lines`); }
          navigate(`/workspace/${workspaceId}/billing#call-lines`, { replace: true });
          setTimeout(() => document.getElementById('call-lines')?.scrollIntoView({ behavior: 'smooth' }), 300);
        }).catch(() => toast.error('We could not confirm the payment yet. Refresh in a moment.'));
    } else if (params.get('lines') === 'cancelled') {
      toast('Checkout cancelled — your lines were not changed.');
    } else if (window.location.hash === '#call-lines') {
      setTimeout(() => document.getElementById('call-lines')?.scrollIntoView({ behavior: 'smooth' }), 400);
      if (params.get('manage') === '1') setOpen(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!info) return null;
  const sub = info.subscription;
  const hasSub = sub.active;
  const cur = hasSub ? sub.lines : 0;
  const changed = extra !== cur || (sub.cancel_at_period_end && extra > 0);

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/api/workspaces/${workspaceId}/call-lines`, { extra, origin_url: window.location.origin });
      if (data.checkout_url) { window.location.href = data.checkout_url; return; }
      setInfo(data); setExtra(data.subscription.active ? data.subscription.lines : 0);
      if (data.celebrate) celebrate();
      toast.success(data.message || 'Call lines updated');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update call lines. Please try again.');
    } finally { setBusy(false); }
  };

  const monthly = (hasSub ? sub.lines : 0) * info.price_per_line;
  const closeDialog = () => { setOpen(false); setExtra(cur); };

  return (
    <section id="call-lines" className="glass-card rounded-2xl p-6 mb-6 scroll-mt-24" data-testid="call-lines-addon">
      <p className="text-base font-semibold text-white mb-1">Add-ons</p>
      <p className="text-xs uppercase tracking-[0.16em] text-gray-500 font-semibold mb-4">Capacity</p>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <PhoneCall size={20} className="text-gray-300 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-white">Concurrent call lines</p>
            <p className="text-sm mt-0.5" data-testid="call-lines-current-total">
              <b className="text-white tabular-nums">{info.limit}</b> <span className="text-gray-300">total</span>
              <span className="text-gray-500 ml-3">{money(monthly)} /mo</span>
            </p>
            {sub.status === 'past_due' && sub.grace_until && (
              <p className="text-xs text-amber-300 mt-1 flex items-center gap-1" data-testid="call-lines-grace-warning">
                <AlertTriangle size={12} />Card declined — update before {fmtDate(sub.grace_until)} or you drop to {info.included} lines
              </p>
            )}
          </div>
        </div>
        <div className="text-right">
          <button type="button" onClick={() => setOpen(true)}
            className="px-4 py-2 rounded-lg bg-teal-400 text-[#06201d] font-semibold text-sm hover:bg-teal-300 transition-colors"
            data-testid="call-lines-manage">Manage lines</button>
          <p className="text-xs text-gray-500 mt-1.5">{money(info.price_per_line)} /mo per line</p>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeDialog())}>
        <DialogContent className={`sm:max-w-2xl w-[calc(100vw-2rem)] p-8 gap-5 ${isDarkMode ? "bg-[#0d1014] border-white/10 text-white" : "bg-white border-gray-200 text-gray-900"}`} data-testid="call-lines-dialog">
          <DialogTitle>Manage lines</DialogTitle>
          <DialogDescription>Review your concurrent call lines and estimated charges for this subscription.</DialogDescription>
          <div className="flex items-start justify-between gap-4 mt-3">
            <div>
              <p className="text-sm font-semibold">Concurrent call lines</p>
              <p className="text-sm text-gray-500">Extra lines on top of the {info.included} included, shared by all sub-accounts.</p>
              <p className="text-sm mt-1"><b>+ {money(info.price_per_line)}/mo each</b> <span className="inline-block w-1.5 h-1.5 rounded-full bg-teal-400 mx-1 align-middle" /><span className="text-gray-500">Bills monthly</span></p>
            </div>
            <Stepper value={extra} min={0} max={info.max_extra} onChange={setExtra} />
          </div>
          <div className="text-sm text-gray-500 space-y-1 mt-2" data-testid="call-lines-new-total">
            <p>Current concurrency: {info.limit}{info.limit > info.included + cur && sub.keep_until ? ` (until ${fmtDate(sub.keep_until)})` : ''}</p>
            <p>New concurrency: {info.included + extra}</p>
          </div>
          {sub.keep_until && new Date(sub.keep_until) > new Date() && (
            <p className="text-xs text-gray-500" data-testid="call-lines-keep-note">You keep {info.included + (sub.keep_extra || 0)} lines until {fmtDate(sub.keep_until)}, then {info.included + sub.lines}.</p>
          )}
          {sub.cancel_at_period_end && (
            <p className="text-xs text-gray-500" data-testid="call-lines-cancel-note">Extra lines end on {fmtDate(sub.period_end)}.</p>
          )}
          <Summary info={info} extra={extra} hasSub={hasSub} />
          <p className="text-xs text-gray-500 flex items-center gap-1.5">
            <CreditCard size={13} />{hasSub ? `Uses your card on file${info.card?.last4 ? ` •••• ${info.card.last4}` : ''}` : 'Secure checkout with Stripe, then you come right back here'}
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={closeDialog} className="px-4 py-2 rounded-lg border border-gray-300/40 text-sm" data-testid="call-lines-cancel">Cancel</button>
            <button type="button" disabled={!changed || busy} onClick={confirm}
              className="px-4 py-2 rounded-lg bg-teal-400 text-[#06201d] font-semibold text-sm disabled:opacity-40 hover:bg-teal-300 transition-colors flex items-center gap-2"
              data-testid="call-lines-confirm">
              {busy && <Loader2 size={15} className="animate-spin" />}{hasSub ? 'Confirm change' : 'Confirm purchase'}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default CallLinesAddon;
